import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Home, Calculator, PieChart, Settings, HelpCircle, ChevronLeft, ChevronRight } from 'lucide-react';

// Set this to false to disable the side navigation
const ENABLE_SIDE_NAV = false;

export default function Layout({ children }: { children: React.ReactNode }) {
  const [isNavOpen, setIsNavOpen] = useState(true);
  const location = useLocation();

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const getBreadcrumbs = () => {
    const paths = location.pathname.split('/').filter(Boolean);
    return [
      { name: 'Home', path: '/' },
      ...paths.map((path, index) => ({
        name: path.charAt(0).toUpperCase() + path.slice(1),
        path: '/' + paths.slice(0, index + 1).join('/')
      }))
    ];
  };

  const siteUrl = "https://debtcalculate.com";
  let path = location.pathname; // location is already available from useLocation()
  // Ensure no trailing slash for non-root paths, and ensure root path is just "/"
  if (path !== '/' && path.endsWith('/')) {
    path = path.slice(0, -1);
  }
  const canonicalUrl = `${siteUrl}${path}`;

  if (!ENABLE_SIDE_NAV) {
    return (
      <>
        <Helmet>
          <link rel="canonical" href={canonicalUrl} />
          {/* Add other page-specific meta tags here if needed in the future */}
        </Helmet>
        <div className="min-h-screen bg-gray-50">
          <header className="bg-white shadow-sm sticky top-0 z-50" role="banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <h1 className="text-xl font-semibold text-gray-900">Debt Calculator</h1>
            </div>
          </div>
        </header>
        <nav aria-label="Breadcrumb" className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <ol className="flex items-center space-x-2 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
              {getBreadcrumbs().map((item, index) => (
                <li key={item.path} itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <div className="flex items-center">
                    {index > 0 && <span className="mx-2 text-gray-400">/</span>}
                    <Link
                      to={item.path}
                      className={`${
                        location.pathname === item.path
                          ? 'text-indigo-600 font-medium'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      itemProp="item"
                    >
                      <span itemProp="name">{item.name}</span>
                    </Link>
                    <meta itemProp="position" content={String(index + 1)} />
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </nav>
        <main className="w-full" role="main">
          {children}
        </main>
      </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
        {/* Add other page-specific meta tags here if needed in the future */}
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm sticky top-0 z-50" role="banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold text-gray-900">Debt Calculator</h1>
          </div>
        </div>
      </header>

      <div className="flex min-h-[calc(100vh-4rem)]">
        <div
          className={`${
            isNavOpen ? 'block lg:hidden' : 'hidden'
          } fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity z-40`}
          onClick={() => setIsNavOpen(false)}
          aria-hidden="true"
        ></div>
        
        <aside 
          className={`
            fixed lg:static inset-y-0 left-0 
            transform ${isNavOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} 
            ${isNavOpen ? 'w-64' : 'w-20'} 
            transition-all duration-300 ease-in-out
            bg-white z-50 h-[calc(100vh-4rem)] mt-16 lg:mt-0
            border-r
          `}
          role="complementary"
        >
          <div className="h-full flex flex-col">
            <div className="h-16 flex items-center justify-between px-4 border-b">
              <span className={`text-xl font-semibold text-gray-800 ${!isNavOpen && 'lg:hidden'}`}>
                Menu
              </span>
              <button
                onClick={() => setIsNavOpen(!isNavOpen)}
                className="hidden lg:flex p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                aria-expanded={isNavOpen}
                aria-label={isNavOpen ? "Collapse sidebar" : "Expand sidebar"}
              >
                {isNavOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
              </button>
            </div>
            <nav className="flex-1 p-4" role="navigation" aria-label="Main navigation">
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className={`flex items-center space-x-3 p-2 rounded-md ${
                      isActivePath('/') 
                        ? 'bg-indigo-50 text-indigo-700' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-current={isActivePath('/') ? 'page' : undefined}
                  >
                    <Calculator size={20} aria-hidden="true" />
                    <span className={!isNavOpen ? 'lg:hidden' : ''}>Debt Calculator</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard"
                    className={`flex items-center space-x-3 p-2 rounded-md ${
                      isActivePath('/dashboard')
                        ? 'bg-indigo-50 text-indigo-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-current={isActivePath('/dashboard') ? 'page' : undefined}
                  >
                    <Home size={20} aria-hidden="true" />
                    <span className={!isNavOpen ? 'lg:hidden' : ''}>Dashboard</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/reports"
                    className={`flex items-center space-x-3 p-2 rounded-md ${
                      isActivePath('/reports')
                        ? 'bg-indigo-50 text-indigo-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-current={isActivePath('/reports') ? 'page' : undefined}
                  >
                    <PieChart size={20} aria-hidden="true" />
                    <span className={!isNavOpen ? 'lg:hidden' : ''}>Reports</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/settings"
                    className={`flex items-center space-x-3 p-2 rounded-md ${
                      isActivePath('/settings')
                        ? 'bg-indigo-50 text-indigo-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-current={isActivePath('/settings') ? 'page' : undefined}
                  >
                    <Settings size={20} aria-hidden="true" />
                    <span className={!isNavOpen ? 'lg:hidden' : ''}>Settings</span>
                  </Link>
                </li>
                <li>
                  <Link
                    to="/help"
                    className={`flex items-center space-x-3 p-2 rounded-md ${
                      isActivePath('/help')
                        ? 'bg-indigo-50 text-indigo-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-current={isActivePath('/help') ? 'page' : undefined}
                  >
                    <HelpCircle size={20} aria-hidden="true" />
                    <span className={!isNavOpen ? 'lg:hidden' : ''}>Help</span>
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </aside>

        <main className={`
          flex-1 overflow-y-auto w-full
          transition-all duration-300
          ${!isNavOpen ? 'lg:ml-20' : 'lg:ml-64'}
        `} role="main">
          <nav aria-label="Breadcrumb" className="bg-white border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
              <ol className="flex items-center space-x-2 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
                {getBreadcrumbs().map((item, index) => (
                  <li key={item.path} itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                    <div className="flex items-center">
                      {index > 0 && <span className="mx-2 text-gray-400">/</span>}
                      <Link
                        to={item.path}
                        className={`${
                          location.pathname === item.path
                            ? 'text-indigo-600 font-medium'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                        itemProp="item"
                      >
                        <span itemProp="name">{item.name}</span>
                      </Link>
                      <meta itemProp="position" content={String(index + 1)} />
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </nav>
          {children}
        </main>
      </div>
    </div>
    </>
  );
}